import _ from "lodash";

import {
    NoteDuration,
    MetronomeMarking,
    TimeSignature
} from "./core";
import { Tonality } from "./scales";


type TimeCut = {
    start: number;
    end: number;
};

type ContextValue<T> = T | ((ContextFrame) => T);
export type ContextProperties = Partial<{
   tonality: ContextValue<Tonality>;
   timeSignature: ContextValue<TimeSignature>;
   metronome: ContextValue<MetronomeMarking>;
}>;

export type ContextProperty = [keyof ContextProperties];

class ContextCell {

    public properties: ContextProperties = {};

    constructor(public start: number, public length: number) {
        if (start < 0 || length <= 0) {
            throw new Error("ContextCell must have a valid start and positive length.");
        }
    }

    get end(): number {
        return this.start + this.length;
    }

    // Checks if the cell intersects a given time cut
    intersects(timePoint: number): boolean {
        return this.start <= timePoint && this.end > timePoint;
    }

    overlaps(existingCell: ContextCell): boolean {
        return (this.start >= existingCell.start && this.start < existingCell.end) || // Overlaps at the start
            (this.end > existingCell.start && this.end <= existingCell.end) || // Overlaps at the end
            (this.start <= existingCell.start && this.end >= existingCell.end);// Fully overlaps

    }

    // toComputedContextFrame(): ComputedContextFrame {
    //     const frame = new ComputedContextFrame();
    //     for (const [key, value] of Object.entries(this.properties)) {
    //         if (value instanceof Function) {
    //             frame[key] = value(frame);
    //         } else {
    //             frame[key] = value;
    //         }
    //     };
    //     return frame;
    // }

    setLength(newLength: number): void {
        if (newLength <= 0) {
            throw new Error("ContextCell length must be a positive integer.");
        }
        this.length = newLength;
    }
}

class ContextRow {
    private cells: ContextCell[] = [];

    constructor(public rowLength: number) {
        if (rowLength <= 0) {
            throw new Error("Row length must be a positive integer.");
        }
    }

    addCell(cell: ContextCell): ContextRow {
        if (cell.end > this.rowLength) {
            throw new Error("ContextCell exceeds the row length.");
        }

        const workingCells = [...this.cells, cell].sort((a, b) => a.start - b.start);
        // workingCells.reduce((previousValue, currentValue) => {
        //     // console.log(previousValue, currentValue);
        // }, null);

        this.cells = [...this.cells, cell].sort((a, b) => a.start - b.start);

        return this;
    }

    // Retrieves all cells in the row
    getCells(): ContextCell[] {
        return this.cells;
    }

    removeCell(cellToRemove: ContextCell): ContextRow {
        this.cells = this.cells.filter(cell => cell !== cellToRemove);
        return this;
    }
}

// Represents the entire context plane
class ContextPlane {
    private rows: ContextRow[] = [];

    constructor(public rowLength: number, public rowCount: number) {
        if (rowLength <= 0 || rowCount <= 0) {
            throw new Error("ContextPlane must have positive row length and row count.");
        }

        this.rows = Array.from({ length: rowCount }, () => new ContextRow(rowLength));
    }

    addCellToRow(rowIndex: number, cell: ContextCell): ContextPlane {
        if (rowIndex < 0 || rowIndex >= this.rows.length) {
            throw new Error("Row index out of bounds.");
        }

        const updatedRow = this.rows[rowIndex].addCell(cell);
        this.rows = [
            ...this.rows.slice(0, rowIndex),
            updatedRow,
            ...this.rows.slice(rowIndex + 1)
        ];
        return this;
    }

    addRow(): ContextPlane {
        console.log('add row');
        this.rows.push(new ContextRow(this.rowLength));
        return this;
    }
    getRows(): ContextRow[] {
        return this.rows;
    }

    removeRow(rowIndex: number): ContextPlane {
        if (rowIndex < 0 || rowIndex >= this.rows.length) {
            throw new Error("Row index out of bounds.");
        }

        this.rows.splice(rowIndex, 1);
        return this;
    }

    getCellsInRow(rowIndex: number): ContextCell[] {
        if (rowIndex < 0 || rowIndex >= this.rows.length) {
            throw new Error("Row index out of bounds.");
        }

        return this.rows[rowIndex].getCells();
    }

    getTimeCuts(limit?: number): TimeCut[] {
        // Collect all start and end points from all cells in all rows
        const points = this.rows
            .flatMap((row) => row.getCells()) // Get all cells from all rows
            .flatMap((cell) => [cell.start, cell.end]); // Collect all start and end points
        if (limit && limit > Math.max(...points)) {
            points.push(limit);
        }

        // Sort the points and remove duplicates
        // we concat the limits of the plane to use as points. Ensures we always get at least one timecut.
        const sortedPoints = Array.from(new Set(points.concat([0, this.rowLength]))).sort((a, b) => a - b);

        // Create all pairs of consecutive points: (n, n+1)
        return sortedPoints.slice(0, -1).map((point, index) => {
            return {
                start: point,
                end: sortedPoints[index + 1] - 1
            } as TimeCut
        });
    }

    generateContextFrames(projectLength?: number): ContextFrame[] {
        const timeCuts = this.getTimeCuts(projectLength);
        return timeCuts.map((timeCut: TimeCut, i) => {
            // Collect all cells from all rows that intersect this time cut
            const frame = this.rows
                .map((row, rowIndex) => row.getCells()
                    .filter((cell) => cell.intersects(timeCut.start))
                    .map((cell) => ({ cell, rowIndex })))
                .flat()
                .sort((a, b) => b.rowIndex - a.rowIndex) // Sort by row number
                .map(({ cell }) => cell);

            return new ContextFrame(timeCut, frame);
        });
    }
}

class ContextFrame {
    constructor(
        public timeCut: TimeCut,
        public cells: ContextCell[]
    ) {}

    get start(): number {
        return this.timeCut.start;
    }

    get end(): number {
        return this.timeCut.end;
    }

    get length(): number {
        return this.end - this.start + 1;
    }

    compute(): ComputedContextFrame {
        const computedFrame = new ComputedContextFrame();
        const timeSignature = this.cells.find(c => c.properties.timeSignature)?.properties.timeSignature;
        const metronome = this.cells.find(c => c.properties.metronome)?.properties.metronome;
        const tonality = this.cells.find(c => c.properties.tonality)?.properties.tonality;

        if (timeSignature instanceof Function) {
            computedFrame.timeSignature = timeSignature(computedFrame);
        } else if (timeSignature) {
            computedFrame.timeSignature = timeSignature;
        }

        if (metronome instanceof Function) {
            computedFrame.metronome = metronome(computedFrame);
        } else if (metronome) {
            computedFrame.metronome = metronome;
        }

        if (tonality instanceof Function) {
            computedFrame.tonality = tonality(computedFrame);
        } else if (tonality) {
            computedFrame.tonality = tonality;
        }

        return computedFrame;
    }
}

/*
// Example usage
const plane = new ContextPlane(10, 3); // A plane with 3 rows, each of length 10

plane.addCellToRow(0, new ContextCell(2, 3)); // Adds a cell from index 2 with length 3 to row 0
plane.addCellToRow(1, new ContextCell(0, 5)); // Adds a cell from index 0 with length 5 to row 1
plane.addCellToRow(2, new ContextCell(7, 2)); // Adds a cell from index 7 with length 2 to row 2

console.log("Time Cuts:", plane.getTimeCuts());
// Example Output: Time Cuts: [ [ 0, 2 ], [ 2, 5 ], [ 5, 7 ], [ 7, 9 ] ]

console.log("Context Frames:", plane.generateContextFrames());
// Example Output: Context Frames:
// [
//   [ ContextCell { start: 0, length: 5 } ],         // Frame for [0, 2]
//   [ ContextCell { start: 0, length: 5 }, ContextCell { start: 2, length: 3 } ], // Frame for [2, 5]
//   [ ContextCell { start: 7, length: 2 } ],         // Frame for [7, 9]
// ]
*/

export class ComputedContextFrame {
    timeSignature: TimeSignature;
    metronome: MetronomeMarking;
    tonality?: Tonality

    static forNewProject(): ComputedContextFrame {
        const cf = new ComputedContextFrame();

        /*  We only set a default for time signature and metronome to simplify the display of bars
            (i.e. there is no case where there's no time signature defined).
            Other values of the context frame are left uninitialized.
        */
        cf.timeSignature = new TimeSignature(4, 4);
        cf.metronome = new MetronomeMarking(90, new NoteDuration(16));
        return cf;
    }
}

export {
    ContextCell,
    ContextRow,
    ContextPlane,
    ContextFrame,
    TimeCut,
    NoteDuration,
}